import {getStagedWorksheetInventoryView} from "./stagedWorksheetInventoryHelpers.js";
import {ToolEngineValidationResult} from "../EngineInterfaces/ToolEngineDataTypes";
import {validateStagedWorksheetInput, validateStagedWorksheetConfig, validateStagedWorksheetPayload} from "./stagedWorksheetValidationHelpers";
import {normalizeLinkedRequirementConfig, normalizeLinkedRequirementPayload, updateLinkedRequirementPayload, getLinkedRequirementView, getLinkedRequirementChanges, getEarlierLinkedRequirementResult, validateLinkedRequirementCompletion} from "./linkedRequirementHelpers.js";
import type {ToolWorkContext} from "./ToolWorkLifecycleTypes";
import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";
import type {
	EnginePayload,
	StagedWorksheetEntryEngineInterface,
	StagedWorksheetSchemaEngineInterface,
	ToolConfig,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	StagedWorksheetEntryEngineInterfaceToken,
	StagedWorksheetSchemaEngineInterfaceToken,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import type {
	ToolInstanceSnapshotData,
	ToolResultSnapshotData,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";

const DEFAULT_STAGE_TITLE = "Stage"
const DEFAULT_ITEM_TYPE = "text"
const DEFAULT_FIELD_RESPONSE_TYPE = "text"

export default class StagedWorksheetToolEngine extends BaseToolEngine implements
	StagedWorksheetSchemaEngineInterface,
	StagedWorksheetEntryEngineInterface {


	normalizeConfig(config: ToolConfig = {}): ToolConfig {
		return this.createStagedWorksheetConfigFromToolConfig(config)
	}

	validateConfig(config: ToolConfig = {}): ToolEngineValidationResult {
		return this.collectWorkValidation(() => validateStagedWorksheetConfig(config))
	}

	validateWorkPayload(payload: EnginePayload, config: ToolConfig): ToolEngineValidationResult {
		return this.collectWorkValidation(() => validateStagedWorksheetPayload(payload, config))
	}

	requiresPreviousWorkPayload(config: ToolConfig = {}): boolean {
		return Array.isArray(config?.entryFields)
	}

	canAccessPublicWork(config: ToolConfig = {}, isOwner = false): boolean {
		return (config.contextMap as ToolConfig | undefined)?.visibility !== "practitioner" || isOwner
	}

	canAccessPublicResult(config: ToolConfig = {}): boolean {
		return (config.contextMap as ToolConfig | undefined)?.visibility !== "practitioner"
	}

	validateWorkInput(payload: EnginePayload, config: ToolConfig): void {
		if (config.gradedPlan || config.orderedAcrossStages || Array.isArray(config.entryFields) || config.contextMap || config.linkedRequirements) {
			validateStagedWorksheetInput(payload, config)
		}
	}

	normalizeWorkPayload(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext): EnginePayload {
		if (Array.isArray(config.entryFields)) {
			const worksheet = {...(payload.stagedWorksheet as ToolConfig || {})}
			const previous = context.previousPayload?.stagedWorksheet as ToolConfig || {}
			for (const key of ["entryFieldSnapshot", "choiceOptionSnapshot", "periodOptionSnapshot"]) {
				worksheet[key] = JSON.parse(JSON.stringify(previous[key] || []))
			}
			payload = {...payload, stagedWorksheet: worksheet}
		}
		return config.gradedPlan || config.orderedAcrossStages || Array.isArray(config.entryFields) || config.contextMap || config.linkedRequirements
			? this.normalizePayload(payload, config) : payload
	}

	finalizeWorkSubmission(payload: EnginePayload, config: ToolConfig): EnginePayload {
		if (config.orderedAcrossStages && !this.hasStagedWorksheetAnchor(payload, config)) {
			throw new Error("Describe a particular situation before submitting this episode")
		}
		if (config.gradedPlan || config.orderedAcrossStages || Array.isArray(config.entryFields) || config.contextMap || config.linkedRequirements) {
			this.assertWorkValidation(this.validateWorkPayload(payload, config))
		}
		if (config.linkedRequirements) validateLinkedRequirementCompletion(payload, config)
		if (config.requireReadyOnSubmit && this.getStagedWorksheetReadiness(payload, config) !== "actionReady") {
			throw new Error("Enter a goal and at least one ordered step before submitting")
		}
		return payload
	}

	getEngineInterfaceTokens() {
		return [
			StagedWorksheetSchemaEngineInterfaceToken,
			StagedWorksheetEntryEngineInterfaceToken,
		]
	}

	createDefaultPayload(config: ToolConfig = {}): EnginePayload {
		return this.createStagedWorksheetEnginePayload({}, this.createStagedWorksheetConfigFromToolConfig(config))
	}

	normalizePayload(enginePayload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		return this.createStagedWorksheetEnginePayload(enginePayload, this.createStagedWorksheetConfigFromToolConfig(config))
	}

	createSummaryInput(enginePayload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		return this.createStagedWorksheetEnginePayload(enginePayload, this.createStagedWorksheetConfigFromToolConfig(config))
	}

	createStagedWorksheetConfigFromToolConfig(config: ToolConfig = {}): ToolConfig {
		const isUnratedGradedPlan = config.gradedPlan === true && config.ratingsEnabled === undefined
		const clientInstructions = isUnratedGradedPlan
			? [config.instructions, ...(Array.isArray(config.guidance) ? (config.guidance as ToolConfig[]).map(row => row.text) : []), config.completionMessage]
				.map(value => String(value || "").trim()).filter(Boolean).join("\n\n")
			: String(config.instructions || "")
		return {
			title: String(config.title || ""),
			gradedPlan: config.gradedPlan === true,
			...(config.requireReadyOnSubmit === undefined ? {} : {requireReadyOnSubmit: config.requireReadyOnSubmit === true}),
			...(config.ratingsEnabled === undefined ? {} : {
				ratingsEnabled: config.ratingsEnabled === true,
				useType: String(config.useType || "gradedTask"),
				taskLabel: String(config.taskLabel || "Graded task"),
				exposureLabel: String(config.exposureLabel || "Graded exposure"),
				ratingLabel: String(config.ratingLabel || "Distress (if relevant)"),
			}),
			instructions: clientInstructions,
			episodePrompt: String(config.episodePrompt || ""),
			anchorConfirmationPrompt: String(config.anchorConfirmationPrompt || ""),
			reactionPrompt: String(config.reactionPrompt || ""),
			observationPrompt: String(config.observationPrompt || ""),
			targetPrompt: String(config.targetPrompt || ""),
			sequenceHeading: String(config.sequenceHeading || ""),
			unknownLabel: String(config.unknownLabel || "Unknown"),
			orderedAcrossStages: config.orderedAcrossStages === true,
			categorizationMode: String(config.categorizationMode || ""),
			categoryQuestion: String(config.categoryQuestion || ""),
			categorySectionTitle: String(config.categorySectionTitle || ""),
			uncategorizedTitle: String(config.uncategorizedTitle || ""),
			removedCategoryMessage: String(config.removedCategoryMessage || ""),
			uncertaintyQuestion: String(config.uncertaintyQuestion || ""),
			focusQuestion: String(config.focusQuestion || ""),
			showPriorResults: !!config.showPriorResults,
			entryLabel: String(config.entryLabel || "Entry"),
			entryPlaceholder: String(config.entryPlaceholder || ""),
			contextTitle: String(config.contextTitle || "Assessment context"),
			choiceTitle: String(config.choiceTitle || "Characterization"),
			...(config.choiceOptions === undefined ? {} : {choiceOptions: this.createStagedWorksheetFieldOptions(config.choiceOptions as ToolConfig[])}),
			periodTitle: String(config.periodTitle || "Relationship period"),
			...(config.periodOptions === undefined ? {} : {periodOptions: this.createStagedWorksheetFieldOptions(config.periodOptions as ToolConfig[])}),
			...(config.entryFields === undefined ? {} : {entryFields: this.createStagedWorksheetFields(config.entryFields as ToolConfig[])}),
			formulationTitle: String(config.formulationTitle || "Practitioner formulation"),
			themeTitle: String(config.themeTitle || "Relationship themes"),
			reportedTitle: String(config.reportedTitle || "Client-reported context"),
			practitionerTitle: String(config.practitionerTitle || "Practitioner observations"),
			completionMessage: isUnratedGradedPlan ? "" : String(config.completionMessage || ""),
			nextStepLabel: String(config.nextStepLabel || ""),
			reviewTitle: String(config.reviewTitle || ""),
			adjustmentPrompt: String(config.adjustmentPrompt || ""),
			incompleteLabel: String(config.incompleteLabel || ""),
			noRemainingLabel: String(config.noRemainingLabel || ""),
			completeLabel: String(config.completeLabel || ""),
			attemptedLabel: String(config.attemptedLabel || ""),
			notCompleteLabel: String(config.notCompleteLabel || ""),
			earlierLabel: String(config.earlierLabel || ""),
			blankStepRows: Math.max(1, Number(config.blankStepRows) || 5),
			guidance: isUnratedGradedPlan ? [] : (Array.isArray(config.guidance) ? config.guidance as ToolConfig[] : []).map((row, index) => ({id: String(row.id || `guidance_${index + 1}`), text: String(row.text || "")})),
			ratingScaleMax: Number(config.ratingScaleMax) || 5,
			stages: this.createStagedWorksheetConfigStages(config.stages as ToolConfig[] | undefined),
			alternativePrompt: String(config.alternativePrompt || "What could be different next time?"),
			...(config.contextMap === undefined ? {} : {contextMap: this.createContextMapConfig(config.contextMap as ToolConfig)}),
			...(config.linkedRequirements === undefined ? {} : {linkedRequirements: normalizeLinkedRequirementConfig(config.linkedRequirements as ToolConfig)}),
			scoring: this.createStagedWorksheetScoringConfig(config.scoring as ToolConfig | undefined),
		}
	}

	createToolConfigFromStagedWorksheetConfig(stagedWorksheetConfig: ToolConfig = {}): ToolConfig {
		return this.createStagedWorksheetConfigFromToolConfig(stagedWorksheetConfig)
	}

	createStagedWorksheetResultConfig(toolResult: ToolResultSnapshotData | null = null, toolInstance: ToolInstanceSnapshotData | null = null): ToolConfig {
		let summary = (toolResult?.summary as ToolConfig | undefined) || {}
		let enginePayload = (toolResult?.enginePayload as ToolConfig | undefined) || {}
		return this.createStagedWorksheetConfigFromToolConfig(toolInstance?.config ?? (summary.stagedWorksheetConfig as ToolConfig | undefined) ?? (enginePayload.stagedWorksheetConfig as ToolConfig | undefined) ?? {})
	}

	createStagedWorksheetEnginePayload(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}): EnginePayload {
		let worksheet = (enginePayload.stagedWorksheet as ToolConfig | undefined) || {}
		let configStages = this.createStagedWorksheetConfigStages(stagedWorksheetConfig.stages as ToolConfig[] | undefined)
		let existingStages = [...(Array.isArray(worksheet.archivedStages) ? worksheet.archivedStages as ToolConfig[] : []), ...(Array.isArray(worksheet.stages) ? worksheet.stages as ToolConfig[] : [])]
		let existingStagesById = new Map(existingStages.map(stage => [String(stage.id || ""), stage]))
		let stages = configStages.map((stage, index) => this.createStagedWorksheetPayloadStage(stage, existingStagesById.get(String(stage.id || "")) || {}, index))
		let itemIds = stages.flatMap(stage => ((stage.items as ToolConfig[]) || []).map(item => String(item.id)))
		let sequenceOrder = (Array.isArray(worksheet.sequenceOrder) ? worksheet.sequenceOrder.map(String) : []).filter(id => itemIds.includes(id))
		sequenceOrder.push(...itemIds.filter(id => !sequenceOrder.includes(id)))
		const result = {
			...enginePayload,
			stagedWorksheet: {
				...(stagedWorksheetConfig.linkedRequirements === undefined ? {} : {linkedRequirements: worksheet.linkedRequirements || {}}),
				context: String(worksheet.context || ""),
				...(stagedWorksheetConfig.contextMap === undefined ? {} : {
					alternatives: this.createStagedWorksheetAlternatives(worksheet.alternatives as ToolConfig[] | undefined),
					promptAnswers: this.createContextMapAnswers(worksheet.promptAnswers as ToolConfig | undefined),
					promptSnapshot: this.mergeStagedWorksheetDefinitions(worksheet.promptSnapshot as ToolConfig[] | undefined, ((stagedWorksheetConfig.contextMap as ToolConfig).prompts as ToolConfig[] | undefined), "id"),
				}),
				theme: String(worksheet.theme || ""),
				entryFieldSnapshot: this.mergeStagedWorksheetDefinitions(worksheet.entryFieldSnapshot as ToolConfig[] | undefined, stagedWorksheetConfig.entryFields as ToolConfig[] | undefined, "id"),
				choiceOptionSnapshot: this.mergeStagedWorksheetDefinitions(worksheet.choiceOptionSnapshot as ToolConfig[] | undefined, stagedWorksheetConfig.choiceOptions as ToolConfig[] | undefined, "value"),
				periodOptionSnapshot: this.mergeStagedWorksheetDefinitions(worksheet.periodOptionSnapshot as ToolConfig[] | undefined, stagedWorksheetConfig.periodOptions as ToolConfig[] | undefined, "value"),
				reviewNote: String(worksheet.reviewNote || ""),
				episodeAnchor: String(worksheet.episodeAnchor || ""),
				episodeIsSpecific: worksheet.episodeIsSpecific === true,
				reaction: String(worksheet.reaction || ""),
				sequenceOrder,
				stages,
				archivedStages: this.createStagedWorksheetArchivedStages(worksheet, configStages),
			},
		}
		return stagedWorksheetConfig.linkedRequirements === undefined ? result : normalizeLinkedRequirementPayload(result)
	}

	getStagedWorksheetInventoryView(payload: EnginePayload = {}, config: ToolConfig = {}): ToolConfig {
		const current = this.createStagedWorksheetEnginePayload(payload, config)
		return getStagedWorksheetInventoryView(current, config)
	}

	getLinkedRequirementView(payload: EnginePayload = {}, config: ToolConfig = {}): ToolConfig {
		return getLinkedRequirementView(this.createStagedWorksheetEnginePayload(payload, config), config)
	}

	getLinkedRequirementChanges(payload: EnginePayload = {}, previousPayload: EnginePayload = {}, config: ToolConfig = {}, previousConfig: ToolConfig = config): ToolConfig {
		return getLinkedRequirementChanges(this.getLinkedRequirementView(payload, config), this.getLinkedRequirementView(previousPayload, previousConfig))
	}

	getEarlierLinkedRequirementResult(current: ToolConfig = {}, candidates: ToolConfig[] = []): ToolConfig | null {
		return getEarlierLinkedRequirementResult(current, candidates)
	}

	updateLinkedRequirementWork(payload: EnginePayload = {}, config: ToolConfig = {}, changes: ToolConfig = {}): EnginePayload {
		return updateLinkedRequirementPayload(this.createStagedWorksheetEnginePayload(payload, config), changes)
	}

	getOrderedStagedWorksheetEntries(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}): ToolConfig[] {
		let worksheet = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig).stagedWorksheet as ToolConfig
		let entries: ToolConfig[] = ((worksheet.stages as ToolConfig[]) || []).flatMap(stage => ((stage.items as ToolConfig[]) || []).map(item => ({...item, stageId: stage.id, stageTitle: stage.title})))
		let order = worksheet.sequenceOrder as string[]
		return entries.sort((a, b) => order.indexOf(String(a.id)) - order.indexOf(String(b.id)))
	}

	addStagedWorksheetEntry(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}, stageId = ""): EnginePayload {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let worksheet = payload.stagedWorksheet as ToolConfig
		let stage = (worksheet.stages as ToolConfig[]).find(stage => stage.id === stageId)
		if (stage) {
			let item = this.createStagedWorksheetItem({id: `item_${Date.now()}_${Math.random().toString(36).slice(2)}`})
			;(stage.items as ToolConfig[]).push(item)
			;(worksheet.sequenceOrder as string[]).push(String(item.id))
		}
		return payload
	}

	updateStagedWorksheetEntry(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}, itemId = "", changes: ToolConfig = {}): EnginePayload {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let stages = (payload.stagedWorksheet as ToolConfig).stages as ToolConfig[]
		let source = stages.find(stage => (stage.items as ToolConfig[]).some(item => item.id === itemId))
		if (!source) return payload
		let item = (source.items as ToolConfig[]).find(item => item.id === itemId) as ToolConfig
		if (changes.stageId && changes.stageId !== source.id) {
			let destination = stages.find(stage => stage.id === changes.stageId)
			if (destination) {
				source.items = (source.items as ToolConfig[]).filter(item => item.id !== itemId)
				;(destination.items as ToolConfig[]).push(item)
			}
		}
		Object.assign(item, {text: changes.text !== undefined ? String(changes.text) : item.text})
		if (changes.values && typeof changes.values === "object" && !Array.isArray(changes.values)) {
			item.values = {...(item.values as ToolConfig || {}), ...(changes.values as ToolConfig)}
		}
		return payload
	}

	removeStagedWorksheetEntry(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}, itemId = ""): EnginePayload {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let worksheet = payload.stagedWorksheet as ToolConfig
		for (let stage of worksheet.stages as ToolConfig[]) {
			stage.items = (stage.items as ToolConfig[]).filter(item => item.id !== itemId)
		}
		worksheet.sequenceOrder = (worksheet.sequenceOrder as string[]).filter(id => id !== itemId)
		return payload
	}

	moveStagedWorksheetEntry(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}, itemId = "", direction = 0): EnginePayload {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let order = (payload.stagedWorksheet as ToolConfig).sequenceOrder as string[]
		let index = order.indexOf(itemId)
		let destination = index + direction
		if (index >= 0 && destination >= 0 && destination < order.length) {
			[order[index], order[destination]] = [order[destination], order[index]]
		}
		return payload
	}

	updateStagedWorksheetContext(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}, changes: ToolConfig = {}): EnginePayload {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let worksheet = payload.stagedWorksheet as ToolConfig
		if (changes.episodeAnchor !== undefined && String(changes.episodeAnchor) !== worksheet.episodeAnchor) {
			worksheet.episodeIsSpecific = false
		}
		for (let key of ["episodeAnchor", "reaction", "context", "theme"]) {
			if (changes[key] !== undefined) worksheet[key] = String(changes[key])
		}
		if (changes.episodeIsSpecific !== undefined) worksheet.episodeIsSpecific = changes.episodeIsSpecific === true
		return payload
	}

	selectStagedWorksheetOption(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}, stageId = "", optionId = "", isSelected = false): EnginePayload {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let stage = ((payload.stagedWorksheet as ToolConfig).stages as ToolConfig[]).find(stage => stage.id === stageId)
		if (!stage || !((stage.options as ToolConfig[] | undefined) || []).some(option => option.value === optionId)) return payload
		let selected = new Set((stage.selectedOptionIds as string[]) || [])
		if (isSelected) selected.add(optionId)
		else selected.delete(optionId)
		stage.selectedOptionIds = Array.from(selected)
		return payload
	}

	hasStagedWorksheetAnchor(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}): boolean {
		let worksheet = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig).stagedWorksheet as ToolConfig
		return !!String(worksheet.episodeAnchor || "").trim() && worksheet.episodeIsSpecific === true
	}

	createStagedWorksheetArchivedStages(worksheet: ToolConfig = {}, configStages: ToolConfig[] = []): ToolConfig[] {
		let activeIds = new Set(configStages.map(stage => String(stage.id)))
		let archived = [...((worksheet.archivedStages as ToolConfig[] | undefined) || []), ...((worksheet.stages as ToolConfig[] | undefined) || [])]
		return Array.from(new Map(archived.map(stage => [String(stage.id), stage])).values()).filter(stage => !activeIds.has(String(stage.id)) && (
			((stage.items as ToolConfig[] | undefined) || []).length ||
			((stage.selectedOptionIds as string[] | undefined) || []).length ||
			((stage.archivedSelections as ToolConfig[] | undefined) || []).length ||
			((stage.alternatives as ToolConfig[] | undefined) || []).length
		))
			.map((stage, index) => this.createStagedWorksheetStage(stage, index))
	}

	createStagedWorksheetConfigStages(stages: ToolConfig[] = []): ToolConfig[] {
		return (Array.isArray(stages) ? stages : []).map((stage, index) => this.createStagedWorksheetConfigStage(stage, index))
	}

	createContextMapConfig(source: ToolConfig = {}): ToolConfig {
		return {
			focusLabel: String(source.focusLabel ?? ""),
			focusPlaceholder: String(source.focusPlaceholder ?? ""),
			effectsLabel: String(source.effectsLabel ?? ""),
			effectsPlaceholder: String(source.effectsPlaceholder ?? ""),
			reflectionLabel: String(source.reflectionLabel ?? ""),
			reflectionPlaceholder: String(source.reflectionPlaceholder ?? ""),
			linkPlaceholder: String(source.linkPlaceholder ?? ""),
			alternativesTitle: String(source.alternativesTitle ?? ""),
			alternativePlaceholder: String(source.alternativePlaceholder ?? ""),
			promptsTitle: String(source.promptsTitle ?? ""),
			unfilledLabel: String(source.unfilledLabel ?? ""),
			coverageLabel: String(source.coverageLabel ?? ""),
			alternativesCountLabel: String(source.alternativesCountLabel ?? ""),
			statusHeading: String(source.statusHeading ?? ""),
			addLinkLabel: String(source.addLinkLabel ?? ""),
			addAlternativeLabel: String(source.addAlternativeLabel ?? ""),
			archivedTitle: String(source.archivedTitle ?? ""),
			gapsTitle: String(source.gapsTitle ?? "Gaps"),
			historyTitle: String(source.historyTitle ?? "Revision history"),
			heldMessage: String(source.heldMessage ?? ""),
			privateNoteLabel: String(source.privateNoteLabel ?? ""),
			visibility: source.visibility === "practitioner" ? "practitioner" : "shared",
			statusOptions: this.createStagedWorksheetFieldOptions(source.statusOptions as ToolConfig[] | undefined),
			prompts: (Array.isArray(source.prompts) ? source.prompts as ToolConfig[] : []).map((prompt, index) => ({
				id: String(prompt.id || `prompt_${index + 1}`),
				text: String(prompt.text ?? ""),
				stageId: String(prompt.stageId || ""),
			})),
		}
	}

	createContextMapAnswers(source: ToolConfig = {}): ToolConfig {
		return Object.fromEntries(Object.entries(source || {}).map(([id, answer]) => [id, String(answer ?? "")]))
	}

	updateContextMapPromptAnswer(payload: EnginePayload = {}, config: ToolConfig = {}, promptId = "", answer = ""): EnginePayload {
		let result = this.createStagedWorksheetEnginePayload(payload, config)
		let worksheet = result.stagedWorksheet as ToolConfig
		let answers = worksheet.promptAnswers as ToolConfig || {}
		worksheet.promptAnswers = {...answers, [promptId]: String(answer)}
		return result
	}

	updateContextMapLinkStatus(payload: EnginePayload = {}, config: ToolConfig = {}, itemId = "", statusId = ""): EnginePayload {
		let map = this.createContextMapConfig(config.contextMap as ToolConfig)
		let option = (map.statusOptions as ToolConfig[]).find(row => row.value === statusId)
		return this.updateStagedWorksheetEntry(payload, config, itemId, {values: {
			statusId: option ? statusId : "",
			statusLabel: option ? String(option.label) : "",
		}})
	}

	addContextMapAlternative(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		let result = this.createStagedWorksheetEnginePayload(payload, config)
		let worksheet = result.stagedWorksheet as ToolConfig
		worksheet.alternatives = [...(worksheet.alternatives as ToolConfig[] || []), this.createStagedWorksheetAlternative({
			id: `alternative_${Date.now()}_${Math.random().toString(36).slice(2)}`,
		})]
		return result
	}

	updateContextMapAlternative(payload: EnginePayload = {}, config: ToolConfig = {}, id = "", text = ""): EnginePayload {
		let result = this.createStagedWorksheetEnginePayload(payload, config)
		let worksheet = result.stagedWorksheet as ToolConfig
		worksheet.alternatives = (worksheet.alternatives as ToolConfig[] || []).map(row => row.id === id ? {...row, text: String(text)} : row)
		return result
	}

	removeContextMapAlternative(payload: EnginePayload = {}, config: ToolConfig = {}, id = ""): EnginePayload {
		let result = this.createStagedWorksheetEnginePayload(payload, config)
		let worksheet = result.stagedWorksheet as ToolConfig
		worksheet.alternatives = (worksheet.alternatives as ToolConfig[] || []).filter(row => row.id !== id)
		return result
	}

	moveContextMapAlternative(payload: EnginePayload = {}, config: ToolConfig = {}, id = "", direction = 0): EnginePayload {
		let result = this.createStagedWorksheetEnginePayload(payload, config)
		let worksheet = result.stagedWorksheet as ToolConfig
		let alternatives = worksheet.alternatives as ToolConfig[] || []
		let index = alternatives.findIndex(row => row.id === id)
		let destination = index + direction
		if (index >= 0 && destination >= 0 && destination < alternatives.length) {
			[alternatives[index], alternatives[destination]] = [alternatives[destination], alternatives[index]]
		}
		return result
	}

	getContextMapView(payload: EnginePayload = {}, config: ToolConfig = {}): ToolConfig {
		let normalizedConfig = this.createStagedWorksheetConfigFromToolConfig(config)
		let map = normalizedConfig.contextMap as ToolConfig || {}
		let worksheet = this.createStagedWorksheetEnginePayload(payload, normalizedConfig).stagedWorksheet as ToolConfig
		let statusOptions = map.statusOptions as ToolConfig[] || []
		let areas = (worksheet.stages as ToolConfig[] || []).map(stage => this.createContextMapAreaView(stage, worksheet, statusOptions))
		return {
			config: map,
			focus: String(worksheet.context || ""),
			effects: String(worksheet.reaction || ""),
			reflection: String(worksheet.theme || ""),
			areas,
			archivedAreas: (worksheet.archivedStages as ToolConfig[] || []).map(stage => this.createContextMapAreaView(stage, worksheet, statusOptions)).filter(stage => (stage.links as ToolConfig[]).length),
			alternatives: (worksheet.alternatives as ToolConfig[] || []).filter(row => String(row.text || "").trim()),
			promptAnswers: worksheet.promptAnswers || {},
			archivedPrompts: (worksheet.promptSnapshot as ToolConfig[] || []).filter(prompt => !(map.prompts as ToolConfig[] || []).some(current => current.id === prompt.id) && String((worksheet.promptAnswers as ToolConfig || {})[String(prompt.id)] || "").trim()),
			unfilledAreas: areas.filter(stage => !(stage.links as ToolConfig[]).length).map(stage => stage.title),
			unansweredPrompts: (map.prompts as ToolConfig[] || []).filter(prompt => !String((worksheet.promptAnswers as ToolConfig || {})[String(prompt.id)] || "").trim()).map(prompt => prompt.text),
			filledAreaCount: areas.filter(stage => (stage.links as ToolConfig[]).length).length,
			areaCount: areas.length,
			alternativeCount: (worksheet.alternatives as ToolConfig[] || []).filter(row => String(row.text || "").trim()).length,
		}
	}

	createContextMapAreaView(stage: ToolConfig, worksheet: ToolConfig, statusOptions: ToolConfig[]): ToolConfig {
		const items = (stage.items as ToolConfig[] || []).filter(item => String(item.text || "").trim())
		const references = [...(worksheet.stages as ToolConfig[] || []), ...(worksheet.archivedStages as ToolConfig[] || [])].flatMap(other => other.items as ToolConfig[] || [])
		const links = items.map(item => {
			const values = item.values as ToolConfig || {}
			const fields = [...(stage.itemFields as ToolConfig[] || []), ...(stage.archivedItemFields as ToolConfig[] || [])]
			const knownIds = new Set(fields.map(field => String(field.id)))
			for (const id of Object.keys(values)) {
				if (!knownIds.has(id) && !["statusId", "statusLabel"].includes(id)) fields.push({id, title: id})
			}
			return {
				...item,
				statusId: String(values.statusId || ""),
				statusLabel: statusOptions.find(option => option.value === values.statusId)?.label || String(values.statusLabel || ""),
				fieldResponses: fields.map(field => {
					const raw = values[String(field.id)]
					const value = raw == null ? "" : typeof raw === "object" ? JSON.stringify(raw) : String(raw)
					const reference = references.find(other => other.id === value)
					return {id: field.id, title: field.title, value: field.responseType === "reference" ? String(reference?.text || (value ? "Earlier entry removed" : "")) : value}
				}),
			}
		})
		return {...stage, items, links}
	}

	getContextMapRevisionHistory(current: ToolConfig = {}, priorResults: ToolConfig[] = [], config: ToolConfig = {}): ToolConfig[] {
		const currentTime = this.getContextMapRevisionTime(current)
		const previous = (Array.isArray(priorResults) ? priorResults : [])
			.filter(result => result.id !== current.id && (!currentTime || !this.getContextMapRevisionTime(result) || this.getContextMapRevisionTime(result) <= currentTime))
			.sort((a, b) => this.getContextMapRevisionTime(a) - this.getContextMapRevisionTime(b))
		const records = [...previous, current]
		return records.map((result, index) => ({
			id: String(result.id || `review_${index + 1}`),
			title: `Review ${index + 1}`,
			changes: index ? this.getContextMapRevisionChanges(records[index - 1].enginePayload as EnginePayload || {}, result.enginePayload as EnginePayload || {}, config) : [],
		}))
	}

	getContextMapRevisionTime(result: ToolConfig = {}): number {
		const date = result.createdAt as ToolConfig | string | number | undefined
		if (date && typeof date === "object" && typeof date.seconds === "number") return date.seconds * 1000
		return new Date(date as string || 0).getTime() || 0
	}

	getContextMapRevisionChanges(beforePayload: EnginePayload = {}, afterPayload: EnginePayload = {}, config: ToolConfig = {}): string[] {
		const before = this.getContextMapView(beforePayload, config)
		const after = this.getContextMapView(afterPayload, config)
		const map = this.createContextMapConfig(config.contextMap as ToolConfig)
		const changes: string[] = []
		for (const [label, key] of [[map.focusLabel, "focus"], [map.effectsLabel, "effects"], [map.reflectionLabel, "reflection"]] as [string, string][]) {
			if (before[key] !== after[key]) changes.push(`${label}: ${String(before[key] || map.unfilledLabel)} → ${String(after[key] || map.unfilledLabel)}`)
		}
		const earlierTargets = (before.alternatives as ToolConfig[] || []).map(row => row.text).join("; ")
		const laterTargets = (after.alternatives as ToolConfig[] || []).map(row => row.text).join("; ")
		if (earlierTargets !== laterTargets) changes.push(`${map.alternativesTitle}: ${earlierTargets || map.unfilledLabel} → ${laterTargets || map.unfilledLabel}`)
		const beforeAreas = before.areas as ToolConfig[] || []
		const afterAreas = after.areas as ToolConfig[] || []
		const areas = [...beforeAreas, ...afterAreas.filter(area => !beforeAreas.some(earlier => earlier.id === area.id))]
		for (const area of areas) {
			const earlier = beforeAreas.find(row => row.id === area.id)
			const later = afterAreas.find(row => row.id === area.id)
			const earlierLinks = (earlier?.links as ToolConfig[] || []).map(link => this.describeContextMapRevisionLink(link)).join("; ")
			const laterLinks = (later?.links as ToolConfig[] || []).map(link => this.describeContextMapRevisionLink(link)).join("; ")
			if (earlierLinks !== laterLinks) changes.push(`${String(area.title)}: ${earlierLinks || map.unfilledLabel} → ${laterLinks || map.unfilledLabel}`)
		}
		const earlierAnswers = before.promptAnswers as ToolConfig || {}
		const laterAnswers = after.promptAnswers as ToolConfig || {}
		for (const prompt of map.prompts as ToolConfig[] || []) {
			const earlierAnswer = String(earlierAnswers[String(prompt.id)] || "")
			const laterAnswer = String(laterAnswers[String(prompt.id)] || "")
			if (earlierAnswer !== laterAnswer) changes.push(`${String(prompt.text)}: ${earlierAnswer || map.unfilledLabel} → ${laterAnswer || map.unfilledLabel}`)
		}
		return changes
	}

	describeContextMapRevisionLink(link: ToolConfig): string {
		const fields = (link.fieldResponses as ToolConfig[] || []).filter(field => field.value).map(field => `${field.title}: ${field.value}`).join(", ")
		return `${String(link.text || "")}${fields ? ` (${fields})` : ""}${link.statusLabel ? ` [${link.statusLabel}]` : ""}`
	}

	createStagedWorksheetSummaryDetails(payload: EnginePayload = {}, config: ToolConfig = {}): ToolConfig {
		if (config.linkedRequirements !== undefined) {
			const view = this.getLinkedRequirementView(payload, config)
			return {headline: String((view.rows as ToolConfig[])[0]?.feature || config.title || ""), linkedRequirementView: view, scoringSummary: null}
		}
		if (config.contextMap === undefined) return {}
		const view = this.getContextMapView(payload, config)
		return {
			headline: String(view.focus || config.title || ""),
			contextMapView: view,
			scoringSummary: null,
		}
	}

	createStagedWorksheetConfigStage(stage: ToolConfig = {}, index = 0): ToolConfig {
		return {
			id: String(stage.id || `stage_${index + 1}`),
			title: String(stage.title || `${DEFAULT_STAGE_TITLE} ${index + 1}`),
			description: String(stage.description || ""),
			itemLabel: String(stage.itemLabel || "Item"),
			itemPlaceholder: String(stage.itemPlaceholder || ""),
			...(stage.showStatus === undefined ? {} : {showStatus: stage.showStatus !== false}),
			allowAlternatives: stage.allowAlternatives !== false,
			categories: this.createStagedWorksheetCategories(stage.categories as ToolConfig[] | undefined),
			minimumItems: Math.max(0, Number(stage.minimumItems) || 0),
			itemFields: this.createStagedWorksheetFields(stage.itemFields as ToolConfig[] | undefined),
			options: this.createStagedWorksheetFieldOptions(stage.options as ToolConfig[] | undefined),
		}
	}

	createStagedWorksheetPayloadStage(configStage: ToolConfig = {}, existingStage: ToolConfig = {}, index = 0): ToolConfig {
		let stage = this.createStagedWorksheetStage({
			...configStage,
			...existingStage,
			id: configStage.id || existingStage.id,
			title: configStage.title || existingStage.title,
			description: configStage.description || existingStage.description,
			itemLabel: configStage.itemLabel || existingStage.itemLabel,
			itemPlaceholder: configStage.itemPlaceholder || existingStage.itemPlaceholder,
			showStatus: configStage.showStatus ?? existingStage.showStatus,
			allowAlternatives: configStage.allowAlternatives ?? existingStage.allowAlternatives,
			categories: configStage.categories || existingStage.categories,
			minimumItems: configStage.minimumItems ?? existingStage.minimumItems,
			itemFields: configStage.itemFields || existingStage.itemFields,
			options: configStage.options || existingStage.options,
		}, index)
		stage.items = this.createStagedWorksheetItems(existingStage.items as ToolConfig[] | undefined)
		stage.alternatives = this.createStagedWorksheetAlternatives(existingStage.alternatives as ToolConfig[] | undefined)
		const activeFieldIds = new Set((stage.itemFields as ToolConfig[] || []).map(field => field.id))
		const previousFields = [...(existingStage.archivedItemFields as ToolConfig[] || []), ...(existingStage.itemFields as ToolConfig[] || [])]
		const archivedFields = previousFields.filter(field => !activeFieldIds.has(field.id)).filter((field, fieldIndex, rows) => rows.findIndex(row => row.id === field.id) === fieldIndex)
		if (archivedFields.length) stage.archivedItemFields = this.createStagedWorksheetFields(archivedFields)
		else delete stage.archivedItemFields
		const options = (stage.options as ToolConfig[] | undefined) || []
		const priorOptions = (existingStage.options as ToolConfig[] | undefined) || []
		const archivedSelections = (existingStage.archivedSelections as ToolConfig[] | undefined) || []
		const selected = new Set([...(Array.isArray(existingStage.selectedOptionIds) ? existingStage.selectedOptionIds as string[] : []), ...archivedSelections.map(option => String(option.value))])
		stage.selectedOptionIds = Array.from(selected).filter(id => options.some(option => option.value === id))
		stage.archivedSelections = Array.from(selected).filter(id => !options.some(option => option.value === id))
			.map(id => archivedSelections.find(option => option.value === id) || priorOptions.find(option => option.value === id) || {value: id, label: id})
		return stage
	}

	createStagedWorksheetStage(stage: ToolConfig = {}, index = 0): ToolConfig {
		return {
			id: String(stage.id || `stage_${index + 1}`),
			title: String(stage.title || `${DEFAULT_STAGE_TITLE} ${index + 1}`),
			description: String(stage.description || ""),
			itemLabel: String(stage.itemLabel || "Item"),
			itemPlaceholder: String(stage.itemPlaceholder || ""),
			...(stage.showStatus === undefined ? {} : {showStatus: stage.showStatus !== false}),
			allowAlternatives: stage.allowAlternatives !== false,
			categories: this.createStagedWorksheetCategories(stage.categories as ToolConfig[] | undefined),
			minimumItems: Math.max(0, Number(stage.minimumItems) || 0),
			itemFields: this.createStagedWorksheetFields(stage.itemFields as ToolConfig[] | undefined),
			...(stage.archivedItemFields === undefined ? {} : {archivedItemFields: this.createStagedWorksheetFields(stage.archivedItemFields as ToolConfig[] | undefined)}),
			options: this.createStagedWorksheetFieldOptions(stage.options as ToolConfig[] | undefined),
			selectedOptionIds: Array.isArray(stage.selectedOptionIds) ? stage.selectedOptionIds.map(String) : [],
			archivedSelections: this.createStagedWorksheetFieldOptions(stage.archivedSelections as ToolConfig[] | undefined),
			items: this.createStagedWorksheetItems(stage.items as ToolConfig[] | undefined),
			alternatives: this.createStagedWorksheetAlternatives(stage.alternatives as ToolConfig[] | undefined),
		}
	}

	createStagedWorksheetFields(fields: ToolConfig[] = []): ToolConfig[] {
		return (Array.isArray(fields) ? fields : []).map((field, index) => this.createStagedWorksheetField(field, index))
	}

	mergeStagedWorksheetDefinitions(previous: ToolConfig[] = [], current: ToolConfig[] = [], idKey = "id"): ToolConfig[] {
		let result = Array.isArray(previous) ? previous.map(row => ({...row})) : []
		for (let row of Array.isArray(current) ? current : []) {
			let index = result.findIndex(previousRow => previousRow[idKey] === row[idKey])
			if (index >= 0) result[index] = {...row}
			else result.push({...row})
		}
		return result
	}

	createStagedWorksheetCategories(categories: ToolConfig[] = []): ToolConfig[] {
		return (Array.isArray(categories) ? categories : []).map((category, index) => ({
			id: String(category.id || `category_${index + 1}`),
			title: String(category.title || ""),
			description: String(category.description || ""),
		}))
	}

	createStagedWorksheetCategoryProfile(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}): ToolConfig {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let stage = ((payload.stagedWorksheet as ToolConfig).stages as ToolConfig[])[0] || {}
		let configStage = (stagedWorksheetConfig.stages as ToolConfig[] || [])[0] || {}
		let categories = this.createStagedWorksheetCategories(configStage.categories as ToolConfig[] || [])
		let items = (stage.items as ToolConfig[] || []).filter(item => String(item.text || "").trim())
		let reviews = ((enginePayload.privatePractitioner as ToolConfig | undefined)?.categoryReviews as ToolConfig | undefined) || {}
		let groups = categories.map(category => {
			let suggested = items.filter(item => ((item.values as ToolConfig).categoryIds as string[] || []).includes(String(category.id)) && !(reviews[String(item.id)] as ToolConfig | undefined)?.isReviewed)
			let reviewed = items.filter(item => ((reviews[String(item.id)] as ToolConfig | undefined)?.categoryIds as string[] || []).includes(String(category.id)) && (reviews[String(item.id)] as ToolConfig | undefined)?.isReviewed)
			return {...category, suggested, reviewed, suggestedCount: suggested.length, reviewedCount: reviewed.length}
		})
		let categoryIds = new Set(categories.map(category => String(category.id)))
		let uncategorized = items.filter(item => {
			let review = (reviews[String(item.id)] as ToolConfig | undefined) || {}
			let selected = (review.isReviewed ? review.categoryIds : (item.values as ToolConfig).categoryIds) as string[] || []
			return !selected.some(id => categoryIds.has(id)) || !!(item.values as ToolConfig).isUncertain
		})
		return {groups, uncategorized, items}
	}

	createStagedWorksheetField(field: ToolConfig = {}, index = 0): ToolConfig {
		return {
			id: String(field.id || `field_${index + 1}`),
			title: String(field.title || `Field ${index + 1}`),
			responseType: String(field.responseType || DEFAULT_FIELD_RESPONSE_TYPE),
			referenceStageId: String(field.referenceStageId || ""),
			placeholder: String(field.placeholder || ""),
			source: String(field.source || "reported"),
			role: String(field.role || "general"),
			options: this.createStagedWorksheetFieldOptions(field.options as ToolConfig[] | undefined),
			isRequired: !!field.isRequired,
		}
	}

	createStagedWorksheetFieldOptions(options: ToolConfig[] = []): ToolConfig[] {
		return (Array.isArray(options) ? options : []).map((option, index) => ({
			label: String(option.label || option.value || `Option ${index + 1}`),
			value: String(option.value || option.label || `option_${index + 1}`),
		}))
	}

	createStagedWorksheetItems(items: ToolConfig[] = []): ToolConfig[] {
		return (Array.isArray(items) ? items : []).map((item, index) => this.createStagedWorksheetItem(item, index))
	}

	createStagedWorksheetItem(item: ToolConfig = {}, index = 0): ToolConfig {
		return {
			id: String(item.id || `item_${Date.now()}_${index}`),
			text: String(item.text || ""),
			notes: String(item.notes || ""),
			history: (Array.isArray(item.history) ? item.history as ToolConfig[] : []).map(entry => ({text: String(entry.text || ""), notes: String(entry.notes || ""), ...(entry.rating === undefined ? {} : {rating: entry.rating})})),
			type: String(item.type || DEFAULT_ITEM_TYPE),
			values: this.createStagedWorksheetItemValues(item.values as ToolConfig | undefined),
		}
	}

	moveStagedWorksheetItem(stage: ToolConfig = {}, fromIndex = 0, toIndex = 0): ToolConfig {
		let items = this.createStagedWorksheetItems(stage.items as ToolConfig[] | undefined)
		if (fromIndex >= 0 && fromIndex < items.length && toIndex >= 0 && toIndex < items.length) {
			let [item] = items.splice(fromIndex, 1)
			items.splice(toIndex, 0, item)
		}
		return {...stage, items}
	}

	updateStagedWorksheetItem(stage: ToolConfig = {}, itemId = "", changes: ToolConfig = {}): ToolConfig {
		let items = this.createStagedWorksheetItems(stage.items as ToolConfig[] | undefined).map(item => {
			if (item.id !== itemId) return item
			let hasAttemptedStep = !!(item.values as ToolConfig).isComplete || !!(item.values as ToolConfig).isAttempted
			let changedText = changes.text !== undefined && String(changes.text) !== item.text
			let shouldArchive = changedText && hasAttemptedStep
			let rating = (item.values as ToolConfig).rating
			let history = shouldArchive && item.text ? [...(item.history as ToolConfig[]), {text: item.text, notes: item.notes, ...(rating === undefined ? {} : {rating})}] : item.history
			let itemChanges = {...changes}
			if (changedText && hasAttemptedStep) {
				itemChanges.values = {...item.values as ToolConfig, isComplete: false, isAttempted: false}
				itemChanges.notes = ""
			}
			return this.createStagedWorksheetItem({...item, ...itemChanges, history})
		})
		return {...stage, items}
	}

	getStagedWorksheetNextItem(stage: ToolConfig = {}): ToolConfig | null {
		return this.createStagedWorksheetItems(stage.items as ToolConfig[] | undefined)
			.find(item => String(item.text).trim() && !(item.values as ToolConfig).isComplete) || null
	}

	getStagedWorksheetReadiness(enginePayload: EnginePayload = {}, config: ToolConfig = {}): string {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, config)
		let stages = (payload.stagedWorksheet as ToolConfig).stages as ToolConfig[]
		let isReady = stages.every(stage => {
			let answered = (stage.items as ToolConfig[]).filter(item => String(item.text || "").trim()).length
			return answered >= Number(stage.minimumItems || 0)
		})
		return isReady ? "actionReady" : "incomplete"
	}

	createStagedWorksheetItemValues(values: ToolConfig = {}): ToolConfig {
		return {
			...(values || {}),
		}
	}

	createStagedWorksheetAlternatives(alternatives: ToolConfig[] = []): ToolConfig[] {
		return (Array.isArray(alternatives) ? alternatives : []).map((alternative, index) => this.createStagedWorksheetAlternative(alternative, index))
	}

	createStagedWorksheetAlternative(alternative: ToolConfig = {}, index = 0): ToolConfig {
		return {
			id: String(alternative.id || `alternative_${Date.now()}_${index}`),
			text: String(alternative.text || alternative.label || ""),
		}
	}

	createStagedWorksheetScoringConfig(scoring: ToolConfig = {}): ToolConfig {
		return {
			frequencyFieldId: String(scoring.frequencyFieldId || ""),
			requiredFieldIds: Array.isArray(scoring.requiredFieldIds) ? scoring.requiredFieldIds.map(String) : [],
			unknownValues: Array.isArray(scoring.unknownValues) ? scoring.unknownValues.map(String) : ["unknown"],
			totals: this.createStagedWorksheetScoringRules(scoring.totals as ToolConfig[] | undefined),
			breakdowns: this.createStagedWorksheetScoringRules(scoring.breakdowns as ToolConfig[] | undefined),
		}
	}

	createStagedWorksheetScoringRules(rules: ToolConfig[] = []): ToolConfig[] {
		return (Array.isArray(rules) ? rules : []).map((rule, index) => ({
			id: String(rule.id || `score_${index + 1}`),
			title: String(rule.title || `Score ${index + 1}`),
			fieldId: String(rule.fieldId || ""),
			values: Array.isArray(rule.values) ? rule.values.map(String) : [],
			options: this.createStagedWorksheetFieldOptions(rule.options as ToolConfig[] | undefined),
			aggregation: String(rule.aggregation || "sumFrequency"),
		}))
	}

	createStagedWorksheetScoringSummary(enginePayload: EnginePayload = {}, stagedWorksheetConfig: ToolConfig = {}): ToolConfig {
		let payload = this.createStagedWorksheetEnginePayload(enginePayload, stagedWorksheetConfig)
		let scoringConfig = this.createStagedWorksheetScoringConfig(stagedWorksheetConfig.scoring as ToolConfig | undefined)
		let entries = this.getStagedWorksheetScorableItems(payload)
		let missingDataCount = this.countStagedWorksheetMissingScoringData(entries, scoringConfig)
		let totals = this.createStagedWorksheetTotalScores(entries, scoringConfig)
		let breakdowns = this.createStagedWorksheetBreakdownScores(entries, scoringConfig)
		return {
			status: this.getStagedWorksheetScoringStatus(entries, missingDataCount),
			entryCount: entries.length,
			missingDataCount,
			totals,
			breakdowns,
		}
	}

	getStagedWorksheetScorableItems(enginePayload: EnginePayload = {}): ToolConfig[] {
		return ((enginePayload.stagedWorksheet as ToolConfig | undefined)?.stages as ToolConfig[] | undefined || [])
			.flatMap(stage => stage.items as ToolConfig[] | undefined || [])
			.filter(item => String(item.text || "").trim() || Object.values((item.values as ToolConfig | undefined) || {}).some(value => value !== "" && value != null))
	}

	countStagedWorksheetMissingScoringData(entries: ToolConfig[] = [], scoringConfig: ToolConfig = {}): number {
		let requiredFieldIds = scoringConfig.requiredFieldIds as string[] | undefined || []
		let unknownValues = new Set(scoringConfig.unknownValues as string[] | undefined || [])
		return entries.reduce((sum, item) => {
			let values = (item.values as ToolConfig | undefined) || {}
			let hasMissingValue = requiredFieldIds.some(fieldId => {
				let value = values[fieldId]
				return value === "" || value == null || unknownValues.has(String(value))
			})
			return sum + (hasMissingValue ? 1 : 0)
		}, 0)
	}

	getStagedWorksheetScoringStatus(entries: ToolConfig[] = [], missingDataCount = 0): string {
		let result = "complete"
		if (!entries.length) {
			result = "unscored"
		} else if (missingDataCount) {
			result = "provisional"
		}
		return result
	}

	createStagedWorksheetTotalScores(entries: ToolConfig[] = [], scoringConfig: ToolConfig = {}): ToolConfig[] {
		return (scoringConfig.totals as ToolConfig[] | undefined || []).map(rule => ({
			...rule,
			value: this.calculateStagedWorksheetRuleValue(entries, scoringConfig, rule),
			entryCount: this.countStagedWorksheetRuleEntries(entries, rule),
		}))
	}

	createStagedWorksheetBreakdownScores(entries: ToolConfig[] = [], scoringConfig: ToolConfig = {}): ToolConfig[] {
		return (scoringConfig.breakdowns as ToolConfig[] | undefined || []).map(rule => ({
			...rule,
			items: this.calculateStagedWorksheetBreakdownItems(entries, scoringConfig, rule),
		}))
	}

	calculateStagedWorksheetBreakdownItems(entries: ToolConfig[] = [], scoringConfig: ToolConfig = {}, rule: ToolConfig = {}): ToolConfig[] {
		let values = new Map<string, number>()
		for (let entry of entries) {
			let itemValues = (entry.values as ToolConfig | undefined) || {}
			let fieldValue = itemValues[String(rule.fieldId || "")]
			if (fieldValue === "" || fieldValue == null) {
				continue
			}
			let valueKey = String(fieldValue)
			values.set(valueKey, (values.get(valueKey) || 0) + this.getStagedWorksheetEntryWeight(entry, scoringConfig))
		}
		return Array.from(values.entries()).map(([value, count]) => ({
			value,
			label: this.getStagedWorksheetScoreOptionLabel(value, rule),
			count,
		}))
	}

	calculateStagedWorksheetRuleValue(entries: ToolConfig[] = [], scoringConfig: ToolConfig = {}, rule: ToolConfig = {}): number {
		return entries.reduce((sum, entry) => {
			return sum + (this.doesStagedWorksheetEntryMatchRule(entry, rule) ? this.getStagedWorksheetEntryWeight(entry, scoringConfig) : 0)
		}, 0)
	}

	countStagedWorksheetRuleEntries(entries: ToolConfig[] = [], rule: ToolConfig = {}): number {
		return entries.filter(entry => this.doesStagedWorksheetEntryMatchRule(entry, rule)).length
	}

	doesStagedWorksheetEntryMatchRule(entry: ToolConfig = {}, rule: ToolConfig = {}): boolean {
		let ruleValues = rule.values as string[] | undefined || []
		let fieldValue = String(((entry.values as ToolConfig | undefined) || {})[String(rule.fieldId || "")] || "")
		return !!fieldValue && (!ruleValues.length || ruleValues.includes(fieldValue))
	}

	getStagedWorksheetEntryWeight(entry: ToolConfig = {}, scoringConfig: ToolConfig = {}): number {
		let frequencyFieldId = String(scoringConfig.frequencyFieldId || "")
		let frequency = Number(((entry.values as ToolConfig | undefined) || {})[frequencyFieldId])
		return Number.isFinite(frequency) && frequency > 0 ? frequency : 1
	}

	getStagedWorksheetScoreOptionLabel(value: string = "", rule: ToolConfig = {}): string {
		let option = (rule.options as ToolConfig[] | undefined || []).find(option => String(option.value || "") === value)
		return String(option?.label || value)
	}
}
